import React, { useEffect, useState, useRef } from 'react';
import { VCCheckGlyph } from './glyphs';
import { t } from '../i18n';

interface Stage05SuccessProps {
  clinicConfig: {
    brand_name: string;
    logo_url?: string;
    booking_url?: string;
    contact_email?: string;
    colors: { primary: string; secondary: string; };
  };
  userEmail: string;
  onCloseWidget: () => void;
}

export const Stage05Success = ({
  clinicConfig,
  userEmail,
  onCloseWidget,
}: Stage05SuccessProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const autoCloseTimerRef = useRef<number | null>(null);
  const [isCardHoveredOrFocused, setIsCardHoveredOrFocused] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const startAutoCloseTimer = () => {
    if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
    autoCloseTimerRef.current = setTimeout(() => {
      if (!isCardHoveredOrFocused) {
        if (cardRef.current) {
          const cardHeight = cardRef.current.offsetHeight;
          cardRef.current.style.height = `${cardHeight}px`;
          requestAnimationFrame(() => {
            cardRef.current!.style.minHeight = '0';
            cardRef.current!.style.maxHeight = '72px';
            cardRef.current!.style.transition = 'max-height var(--dur-slow) var(--ease-standard), opacity var(--dur-slow) var(--ease-standard)';
            cardRef.current!.style.overflow = 'hidden';
            cardRef.current!.style.opacity = '0.9';
          });
          setTimeout(() => {
            setIsCollapsed(true);
            onCloseWidget();
          }, parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--dur-slow')) || 350);
        }
      }
    }, 8000);
  };

  useEffect(() => {
    startAutoCloseTimer();

    const handleInteraction = () => setIsCardHoveredOrFocused(true);
    const handleNoInteraction = () => setIsCardHoveredOrFocused(false);

    const cardElement = cardRef.current;
    if (cardElement) {
      cardElement.addEventListener('mouseenter', handleInteraction);
      cardElement.addEventListener('mouseleave', handleNoInteraction);
      cardElement.addEventListener('focusin', handleInteraction);
      cardElement.addEventListener('focusout', handleNoInteraction);
    }

    return () => {
      if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
      if (cardElement) {
        cardElement.removeEventListener('mouseenter', handleInteraction);
        cardElement.removeEventListener('mouseleave', handleNoInteraction);
        cardElement.removeEventListener('focusin', handleInteraction);
        cardElement.removeEventListener('focusout', handleNoInteraction);
      }
    };
  }, [isCardHoveredOrFocused]);

  const brandMark = clinicConfig.logo_url ? (
    <img src={clinicConfig.logo_url} alt={clinicConfig.brand_name} />
  ) : (
    clinicConfig.brand_name
  );

  return (
    <div
      className={`vc-frame stage-05-success ${isCollapsed ? 'is-collapsed' : ''}`}
      ref={cardRef}
      tabIndex={-1}
    >
      <style>{`
        /* Stage 05 — stage-specific layout only.
           Shared chamber vocabulary (seal, emailpill, divider, bar)
           → widget-components.css */
        .stage-05-success.is-collapsed {
          min-height: 0;
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: var(--sp-3);
          background: var(--bg-chamber-base);
          border: 1px solid var(--border-on-chamber);
          border-radius: var(--radius-pill);
          box-shadow: var(--shadow-xl), var(--scan-glow);
          padding: var(--sp-3) var(--sp-6);
          justify-content: center;
        }
        .stage-05-success.is-collapsed .vc-bar-brand {
          font-size: var(--text-sm);
          font-weight: var(--weight-semibold);
          letter-spacing: var(--tracking-widest);
          text-transform: uppercase;
          color: var(--scan-line);
          white-space: nowrap;
        }
        .stage-05-success.is-collapsed .vc-bar-brand img {
          max-height: var(--sp-5);
          object-fit: contain;
        }
        .stage-05-success.is-collapsed .vc-bar-status {
          font-size: var(--text-sm);
          color: var(--accent-200);
          display: inline-flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
        }
        .stage-05-success .vc-success-zone {
          display: flex;
          flex-direction: column;
          gap: var(--sp-4);
          margin-top: var(--sp-6);
        }
        .stage-05-success .vc-seal-glyph {
          display: grid;
          place-items: center;
        }
        @keyframes vc-seal-pop {
          from { opacity: 0; transform: scale(0.8); }
          to   { opacity: 1; transform: scale(1); }
        }
        .stage-05-success .vc-seal {
          animation: vc-seal-pop var(--dur-slow) var(--ease-entrance) both;
        }
        @media (prefers-reduced-motion: reduce) {
          .stage-05-success .vc-seal { animation: none; }
        }
      `}</style>

      <div aria-live="assertive" aria-atomic="true" className="visually-hidden">
        {t('success.live')}
      </div>

      {!isCollapsed && (
        <>
          <div className="vc-wordmark" style={{ marginTop: 'var(--sp-5)' }}>{brandMark}</div>

          <div className="vc-seal" aria-hidden="true">
            <span className="vc-seal-glyph">
              <VCCheckGlyph size={40} strokeWidth={2} />
            </span>
          </div>

          <h2 className="vc-title" role="heading" aria-level={2} style={{ marginTop: 'var(--sp-8)' }}>
            {t('success.title.line1')}<br /><em>{t('success.title.em')}</em>
          </h2>

          <p className="vc-sub">
            {t('success.sub.pre')}
            <span className="vc-emailpill">{userEmail}</span>
            {t('success.sub.post')}
          </p>

          <div className="vc-divider"></div>

          {clinicConfig.booking_url && (
            <div className="vc-success-zone">
              <p className="vc-sub" style={{ margin: '0 var(--sp-8)' }}>
                {t('success.talk')}
              </p>
              <a
                href={clinicConfig.booking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="vc-btn vc-btn--outline"
                style={{ textDecoration: 'none' }}
                aria-label={t('success.bookAria', { brand: clinicConfig.brand_name || t('common.theClinic') })}
                onClick={() => {
                  if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
                  autoCloseTimerRef.current = setTimeout(() => startAutoCloseTimer(), 30000);
                }}
              >
                {t('success.book')}
              </a>
            </div>
          )}

          {clinicConfig.contact_email && (
            <p className="vc-legal" style={{ marginTop: 'auto', paddingTop: 'var(--sp-6)', paddingBottom: 'var(--sp-6)' }}>
              {t('success.help')}
              <a
                href={`mailto:${clinicConfig.contact_email}`}
                aria-label={t('success.emailAria', { brand: clinicConfig.brand_name || t('common.theClinic') })}
              >
                {t('success.contactLink')}
              </a>
            </p>
          )}
        </>
      )}

      {isCollapsed && (
        <>
          <span className="vc-bar-brand">{brandMark}</span>
          <span className="vc-bar-status">
            <VCCheckGlyph size={12} strokeWidth={3} /> {t('success.barStatus')}
          </span>
        </>
      )}
    </div>
  );
};
